<template>
    <VModalWrapper :name="name" :title="title" v-on="listeners" v-bind="attributes">
        <!-- ПЕРВЫЙ ШАГ СОЗДАНИЯ: что заводим и сколько штук. Два вопроса, и оба
             с готовым ответом по умолчанию — «без шаблона» и «1». Сотрудник склада
             должен понять экран без инструкции: слева то, чем он пользуется каждый день
             (избранное), ниже — всё остальное. -->
        <div class="template-picker">
            <div class="template-picker__loader" v-if="isLoading">
                <VSpinner></VSpinner>
            </div>

            <template v-else>
                <!-- Поиск виден всегда: шаблонов ожидается несколько десятков, и прятать
                     его до какого-то их числа значит заставлять человека сначала
                     обнаружить, что список длинный. -->
                <div class="template-picker__search">
                    <BaseToolsSearch
                        placeholder="Поиск по шаблонам"
                        v-model="searchInput"
                        @submit="onSearchSubmit"
                    ></BaseToolsSearch>
                </div>

                <section class="template-picker__group" v-if="favorites.length">
                    <h3 class="template-picker__group-title">Избранные</h3>

                    <ul class="template-picker__grid">
                        <li v-for="preset in favorites" :key="`fav-${preset.id}`">
                            <VInventoryTemplateCard
                                :preset="preset"
                                :selected="isSelected(preset)"
                                :busy="favoriteBusyId === preset.id"
                                @select="onSelect"
                                @favorite="onToggleFavorite"
                            ></VInventoryTemplateCard>
                        </li>
                    </ul>
                </section>

                <section class="template-picker__group">
                    <h3 class="template-picker__group-title">Все шаблоны</h3>

                    <p class="template-picker__empty" v-if="!presets.length">
                        Шаблонов пока нет. Оборудование можно завести без шаблона.
                    </p>

                    <!-- Ограниченная по высоте область: тридцать шаблонов уводили бы
                         количество и кнопки на два экрана вниз, и до них приходилось бы
                         долистывать. Избранное остаётся снаружи: оно короткое и должно
                         быть видно сразу. -->
                    <ul class="template-picker__grid template-picker__grid_scroll" v-else>
                        <li v-for="preset in presets" :key="preset.id">
                            <VInventoryTemplateCard
                                :preset="preset"
                                :selected="isSelected(preset)"
                                :busy="favoriteBusyId === preset.id"
                                @select="onSelect"
                                @favorite="onToggleFavorite"
                            ></VInventoryTemplateCard>
                        </li>
                    </ul>
                </section>

                <!-- «БЕЗ ШАБЛОНА» ЗДЕСЬ БОЛЬШЕ НЕТ (7-D2). Создание идёт только через
                     шаблон: он задаёт состав формы, и вариант в обход него означал бы
                     вторую ветку формы с собственным набором полей — ровно то, от чего
                     уходили, убирая старую карточку создания. Нужен универсальный
                     сценарий — заводится обычный шаблон «Универсальный», и он работает
                     тем же путём. -->

                <!-- КОЛИЧЕСТВО. Здесь, а не на следующем шаге: от него зависит, какая
                     форма откроется дальше, и спрашивать его после общих полей значило бы
                     менять форму под человеком. -->
                <section class="template-picker__count">
                    <label class="template-picker__count-label" for="template-count">Количество единиц</label>

                    <div class="template-picker__count-controls">
                        <button
                            type="button"
                            class="template-picker__step"
                            title="Меньше"
                            :disabled="count <= 1"
                            @click="step(-1)"
                        >
                            −
                        </button>

                        <input
                            id="template-count"
                            class="template-picker__count-input control"
                            type="number"
                            inputmode="numeric"
                            min="1"
                            :max="maxBatchSize"
                            v-model.number="count"
                        />

                        <button
                            type="button"
                            class="template-picker__step"
                            title="Больше"
                            :disabled="count >= maxBatchSize"
                            @click="step(1)"
                        >
                            +
                        </button>
                    </div>

                    <p class="template-picker__count-hint">{{ countHint }}</p>

                    <p class="template-picker__blocked" v-if="blockedReason">{{ blockedReason }}</p>
                </section>

                <div class="template-picker__actions">
                    <VButton type="button" class="button_green-light" @click="hide">Отмена</VButton>

                    <VButton type="button" class="button_green" :disabled="!canContinue" @click="submit">
                        Продолжить
                    </VButton>
                </div>
            </template>
        </div>
    </VModalWrapper>
</template>
<script>
// Components
import VButton from '../../../components/VButton/VButton.vue';
import VSpinner from '../../../components/VProgress/VSpinner.vue';
import BaseToolsSearch from '../../../components/tools/BaseToolsSearch.vue';
import VInventoryTemplateCard from '../components/VInventoryTemplateCard.vue';
import VModalWrapper from '../../../components/VModalWrapper/VModalWrapper.vue';

// Other
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';
import ModalNames from '../../../../js/enums/ModalNames';
import ModalMixin from '../../../../js/vue/mixins/ModalMixin';
import { errorMessage } from '../../../../js/error';
import { MAX_BATCH_SIZE } from '../../../../js/inventoryTemplate';
import { paginateInventoryPresets, setInventoryPresetFavorite } from '../../../../js/inventory-api';

/**
 * Выбор шаблона и количества — первый шаг создания оборудования.
 *
 * ПОЧЕМУ КАРТОЧКИ, А НЕ ВЫПАДАЮЩИЙ СПИСОК. Прежний экран был одним select с поиском:
 * чтобы выбрать шаблон, его надо было вспомнить по названию. Шаблон теперь описывает тип
 * техники, и таких типов у склада десятки — но каждый день нужны пять. Карточки
 * показывают их сразу, а избранное поднимает те пять наверх.
 *
 * ИЗБРАННОЕ ПЕРСОНАЛЬНО и живёт на бэкенде (PUT /presets/{id}/favorite): localStorage
 * не годится — человек работает с двух устройств, и список, собранный на складе,
 * должен быть тем же в кабинете.
 */
@Component({
    components: {
        VButton,
        VSpinner,
        VModalWrapper,
        BaseToolsSearch,
        VInventoryTemplateCard,
    },
})
class VInventoryTemplatePickerModal extends ModalMixin {
    @Prop({ type: String, default: ModalNames.INVENTORY_TEMPLATE_PICKER })
    name;
    @Prop({ type: String, default: 'Новое оборудование' })
    defaultTitle;

    presets = [];
    favorites = [];
    isLoading = false;
    searchInput = '';
    searchString = '';
    favoriteBusyId = null;

    // null — «ещё не выбрали». Создание без шаблона больше не существует, поэтому
    // продолжить с этим значением нельзя (см. isCountValid и submit).
    selected = null;
    count = 1;

    get maxBatchSize() {
        return MAX_BATCH_SIZE;
    }

    get isCountValid() {
        const value = Number(this.count);

        return Number.isInteger(value) && value >= 1 && value <= MAX_BATCH_SIZE;
    }

    /**
     * Можно ли продолжить: шаблон выбран и количество осмысленное.
     */
    get canContinue() {
        return Boolean(this.selected) && this.isCountValid;
    }

    /**
     * Почему кнопка недоступна. Объяснение вместо молчаливо погашенной кнопки:
     * «почему нельзя нажать» человек иначе выясняет перебором.
     */
    get blockedReason() {
        if (!this.selected) {
            return 'Выберите шаблон — по нему строится форма создания.';
        }

        return this.isCountValid ? '' : `Введите количество от 1 до ${MAX_BATCH_SIZE}.`;
    }

    /**
     * Подсказка под количеством: она объясняет, какая форма откроется следующей.
     * Без неё переход в режим партии выглядит как другая программа.
     */
    get countHint() {
        if (!this.isCountValid) {
            return `Введите число от 1 до ${MAX_BATCH_SIZE}.`;
        }

        return Number(this.count) > 1
            ? 'Откроется создание партии: общие поля заполняются один раз, серийные номера — списком.'
            : 'Откроется форма новой единицы: одно наименование и один серийный номер.';
    }

    isSelected(preset) {
        return Boolean(this.selected) && this.selected.id === preset.id;
    }

    onSelect(preset) {
        this.selected = preset;
    }

    step(delta) {
        const next = Number(this.count) + delta;

        this.count = Math.min(MAX_BATCH_SIZE, Math.max(1, Number.isFinite(next) ? next : 1));
    }

    onSearchSubmit() {
        this.searchString = this.searchInput;
        this.fetchPresets();
    }

    /**
     * Звезда. Список перечитывается целиком, а не правится на месте: избранное меняет
     * состав ПЕРВОЙ группы, и пересобрать её вручную значит завести второе правило
     * сортировки рядом с серверным.
     *
     * @param {Object} preset
     */
    async onToggleFavorite(preset) {
        if (this.favoriteBusyId) {
            return;
        }

        this.favoriteBusyId = preset.id;

        try {
            await setInventoryPresetFavorite(preset.id, !preset.is_favorite);
            await this.fetchPresets();
        } catch (error) {
            this.$showModal(ModalNames.ERROR, { errorMessage: errorMessage(error) });
        } finally {
            this.favoriteBusyId = null;
        }
    }

    async fetchPresets() {
        this.isLoading = this.presets.length === 0;

        try {
            // Два запроса вместо одного с последующим разбором: «избранные» — это
            // отдельная выборка с собственной страницей, и вырезать её из общего списка
            // значило бы показывать не то, что показывает фильтр.
            const [all, favorites] = await Promise.all([
                paginateInventoryPresets(1, this.searchString, 100),
                paginateInventoryPresets(1, '', 100, { onlyFavorites: true }),
            ]);

            this.presets = all.data.data ?? [];
            this.favorites = favorites.data.data ?? [];
        } catch (error) {
            this.$showModal(ModalNames.ERROR, { errorMessage: errorMessage(error) });
        } finally {
            this.isLoading = false;
        }
    }

    submit() {
        if (!this.canContinue) {
            return;
        }

        this.resolve({ preset: this.selected, count: Number(this.count) });
        this.hide();
    }

    beforeOpen() {
        this.selected = null;
        this.count = 1;
        this.searchInput = '';
        this.searchString = '';
        this.fetchPresets();
    }

    beforeClose() {
        this.favoriteBusyId = null;
    }
}

export default VInventoryTemplatePickerModal;
</script>
<style lang="scss">
// Выбор шаблона. СНАЧАЛА УЗКИЙ ЭКРАН: раздел открывают с телефона у стеллажа,
// и сетка карточек рассчитана на такую ширину — на широкой их просто становится больше.
.template-picker {
    &__loader {
        display: flex;
        justify-content: center;
        padding: 32px 0;
    }

    &__search {
        margin-bottom: 14px;
    }

    &__group {
        margin-bottom: 18px;
    }

    &__group-title {
        margin: 0 0 8px;
        color: #5f6368;
        font-size: 0.75rem;
        font-weight: 500;
        text-transform: uppercase;
        letter-spacing: 0.04em;
    }

    &__empty {
        margin: 0;
        color: #5f6368;
        font-size: 0.8125rem;
    }

    // auto-fill с минимумом в 200 точек: на телефоне одна колонка, дальше — сколько войдёт.
    // Явного числа колонок нет намеренно, ширина модалки зависит от экрана.
    &__grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
        gap: 10px;
        margin: 0;
        padding: 0;
        list-style: none;

        // Высота в единицах экрана, а не в точках: на телефоне 40vh это примерно три
        // карточки, на ноутбуке — шесть, и в обоих случаях под списком остаётся место
        // для количества и кнопок. Числом в точках этого не выразить.
        &_scroll {
            max-height: 40vh;
            overflow-y: auto;
            overscroll-behavior-y: contain;
            -webkit-overflow-scrolling: touch;
            // Полоса прокрутки не должна прижиматься к карточкам вплотную.
            padding-right: 4px;
        }
    }

    // Причина недоступности — не ошибка ввода, а объяснение: красный здесь врал бы.
    &__blocked {
        margin: 4px 0 0;
        color: #5f6368;
        font-size: 0.75rem;
    }

    &__count-hint {
        margin: 6px 0 0;
        color: #9aa0a6;
        font-size: 0.75rem;
    }

    &__actions {
        position: sticky;
        bottom: 0;
        display: flex;
        justify-content: flex-end;
        gap: 10px;
        padding-bottom: 2px;
        margin-top: 18px;
        background: #fff;
    }
}
</style>
